import React from 'react';
import axios from '../../../modules/utils/axios.utils';
import getBrowserTimeZone from '../../../modules/shared/utils/returnTimeZone';

interface AutoBulkFiltersContextInterface {
  fetchFilterOptions?: (filterParams?: string) => Promise<void>;
  filterOptions?: Record<string, string[]>;
  filterOptionsLoading?: boolean;
  transactionCharts?: Record<string, any>;
  transactionChartsLoading?: boolean;
  errorMessage?: string;

  convertFilterValuesToUrlParams?: (machines: string[], customFilters: any) => string;

  convertProductFilterValuesToUrlParams?: (
    startDate: string,
    endDate: string,
    machines: string[],
    customFilters: any,
  ) => string;
}

const AutoBulkFiltersContext = React.createContext<AutoBulkFiltersContextInterface>({});

const AutoBulkFiltersContextConsumer = AutoBulkFiltersContext.Consumer;
const AutoBulkFiltersContextProvider: React.FC = ({ children }) => {
  const [filterOptions, setFilterOptions] = React.useState<Record<string, string[]>>({});
  const [filterOptionsLoading, setFilterOptionsLoading] = React.useState<boolean>(false);

  const [errorMessage, setErrorMessage] = React.useState<string>('');

  const fetchFilterOptions = async (filterParams = '') => {
    setErrorMessage('');
    setFilterOptionsLoading(true);

    const cachedMachines = JSON.parse(localStorage.getItem('autoBulkMachines') || '[]');
    const cachedTimePeriod = localStorage.getItem('timePeriod') || '';
    const cachedStartDate = localStorage.getItem('startDate');
    const cachedEndDate = localStorage.getItem('endDate');

    let timeParams = {};
    const browser_tz = getBrowserTimeZone();

    if (cachedStartDate && cachedEndDate) {
      timeParams =
        cachedTimePeriod !== 'Custom'
          ? { time_period: cachedTimePeriod || '' }
          : {
              work_order_start_date_after: cachedStartDate,
              work_order_start_date_before: cachedEndDate,
            };
    } else {
      timeParams = { time_period: cachedTimePeriod || '' };
    }

    const params = new URLSearchParams({
      machine: cachedMachines.join(),
      ...Object.fromEntries(new URLSearchParams(filterParams)),
      ...timeParams,
      browser_tz,
    }).toString();

    axios
      .get<string, any>(`api/autobulk/analytics/filters/?${params}`, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem('accessToken')}`,
        },
      })
      .then((response) => {
        setFilterOptions({
          customer_names_list: response.data.customer_names_list,
          farm_notes_list: response.data.farm_notes_list,
          field_notes_list: response.data.field_notes_list,
          product_name_list: response.data.product_name_list,
          product_type_list: response.data.product_type_list,
          sources_list: response.data.sources_list,
        });
        setFilterOptionsLoading(false);
      })
      .catch(() => {
        setFilterOptionsLoading(false);
        setErrorMessage('Something went wrong. Please reload the page.');
      });
  };

  const fetchProductFilterOptions = async (filterParams = '') => {
    setFilterOptionsLoading(true);

    const cachedMachines = JSON.parse(localStorage.getItem('autoBulkMachines') || '[]');

    // Get dates from localStorage and handle them to prevent timezone issues
    let cachedStartDate = localStorage.getItem('startDate');
    let cachedEndDate = localStorage.getItem('endDate');

    // If dates include time component or special formatting, extract just the date part
    if (cachedStartDate && (cachedStartDate.includes('T') || cachedStartDate.includes(' '))) {
      cachedStartDate = cachedStartDate.split(/[T\s]/)[0];
    }

    if (cachedEndDate && (cachedEndDate.includes('T') || cachedEndDate.includes(' '))) {
      cachedEndDate = cachedEndDate.split(/[T\s]/)[0];
    }

    // Add noon time to prevent timezone boundary issues
    const startDateForApi = cachedStartDate ? `${cachedStartDate} 12:00:00` : '';
    const endDateForApi = cachedEndDate ? `${cachedEndDate} 12:00:00` : '';

    // Create params with the proper date format
    const params = new URLSearchParams({
      transaction__work_order_start_date_after: startDateForApi,
      transaction__work_order_start_date_before: endDateForApi,
      machine: cachedMachines.join(),
      ...Object.fromEntries(new URLSearchParams(filterParams)),
    }).toString();

    axios
      .get<string, any>(`api/autobulk/analytics/transactions/product_filters/?${params}`, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem('accessToken')}`,
        },
      })
      .then((response) => {
        setFilterOptions(response.data);
        setFilterOptionsLoading(false);
      })
      .catch(() => {
        setFilterOptionsLoading(false);
        setErrorMessage('Something went wrong. Please reload the page.');
      });
  };

  const convertFilterValuesToUrlParams = (machines: string[], customFilters: any) => {
    let filterValues = '';

    if (machines) {
      filterValues += `machine=${machines?.join()}&`;
    }

    if (customFilters?.customers?.length > 0) {
      filterValues += `customer_name=${encodeURIComponent(customFilters.customers?.join('|~|'))}&`;
    }

    if (customFilters?.farm?.length > 0) {
      filterValues += `work_order_farm_notes=${encodeURIComponent(customFilters.farm?.join('|~|'))}&`;
    }

    if (customFilters?.field?.length > 0) {
      filterValues += `work_order_field_notes=${encodeURIComponent(customFilters.field?.join('|~|'))}&`;
    }

    if (customFilters?.products?.length > 0) {
      filterValues += `products=${encodeURIComponent(customFilters.products?.join('|~|'))}&`;
    }

    if (customFilters?.sources?.length > 0) {
      filterValues += `sources=${encodeURIComponent(customFilters.sources?.join('|~|'))}&`;
    }

    if (customFilters?.product_types?.length > 0) {
      filterValues += `product_types=${encodeURIComponent(customFilters.product_types?.join('|~|'))}&`;
    }

    if (customFilters?.work_order_name?.length > 0) {
      filterValues += `work_order_name=${encodeURIComponent(customFilters.work_order_name)}&`;
    }

    return filterValues;
  };

  return (
    <AutoBulkFiltersContext.Provider
      value={{
        fetchFilterOptions,
        filterOptions,
        filterOptionsLoading,
        errorMessage,
        convertFilterValuesToUrlParams,
        // convertProductFilterValuesToUrlParams,
      }}
    >
      {children}
    </AutoBulkFiltersContext.Provider>
  );
};

export { AutoBulkFiltersContextProvider, AutoBulkFiltersContextConsumer, AutoBulkFiltersContext };
